import { gnssResultSegments, gnssMocLabel, gnssMissedParameters, gnssHasObstacleResults, gnssObstacleCounts } from './gnssResultSegments';

// Append the same evaluated GNSS data used by the HTML result panel. Geometry
// and obstacle calculations stay in the backend; this module only formats them.
const INPUT_BLUE = [100, 181, 246];
const OUTPUT_BLUE = [32, 114, 184];
const CRITICAL_RED = [183, 28, 28];

const display = value => value == null || value === '' ? '-' : String(value);
const numeric = value => (typeof value === 'number' || (typeof value === 'string' && value.trim() !== ''))
  && Number.isFinite(Number(value));
const fixed = (value, digits = 2) => numeric(value) ? Number(value).toFixed(digits) : '-';
const divide = (value, divisor) => numeric(value) ? Number(value) / divisor : null;
const altitude = (metres, feet) => !numeric(metres) && !numeric(feet) ? '-'
  : `${fixed(metres)} m\n${fixed(feet)} ft`;
const minimum = value => value?.value == null ? '-'
  : `${display(value.value)} ${value.unit || ''}`.trim();

function procedureLabel(input, includeCategory = true) {
  return `${display(input.airport_code)} / RWY ${display(input.runway_designator)}`
    + ` | ${display(input.approach_id)} | ${display(input.approach_type)}`
    + (includeCategory ? ` | Cat ${display(input.aircraft_category)}` : '');
}

function segmentRoute(segment, input) {
  if (segment.baroSurface || segment.lpvSurface || segment.xlsSurface) {
    return `${display(segment.fromPhase)} -> ${display(segment.toPhase)}`;
  }
  const family = String(segment.segFamily || '').toLowerCase();
  if (family === 'initial') {
    const index = String(segment.segLabel || '').match(/initial\s*(\d+)/i)?.[1];
    if (index && input[`iafwp${index}_ident`] && input.iwp_ident) {
      return `${input[`iafwp${index}_ident`]} -> ${input.iwp_ident}`;
    }
  }
  if (family === 'intermediate' && input.iwp_ident) {
    return `${input.iwp_ident} -> ${segment.toPhase === 'FAP' ? 'FAP' : display(input.fawp_ident)}`;
  }
  if (family === 'final' && input.fawp_ident && input.mawp_ident) {
    return `${input.fawp_ident} -> ${input.mawp_ident}`;
  }
  return `${display(segment.fromPhase)} -> ${display(segment.toPhase)}`;
}

/** Categories with obstacle results receive tables, independently of map layer visibility. */
export function appendGnssApproachReport({
  doc, analysis, t, startY, contentAreaTop, marginBottom, footerHeight,
}) {
  if (!analysis) return startY;
  const allResults = Array.isArray(analysis.results) ? analysis.results : [];
  const results = allResults.filter(gnssHasObstacleResults);
  const obstacleCountsByResult = new Map(results.map(result => [result, gnssObstacleCounts(result)]));
  const sv = t('language') === 'Svenska';
  const label = (swedish, english) => sv ? swedish : english;
  const yesNo = value => value == null ? '-' : value ? label('Ja', 'Yes') : label('Nej', 'No');
  const pageWidth = doc.internal.pageSize.getWidth();
  const bottom = doc.internal.pageSize.getHeight() - marginBottom - footerHeight;
  const left = 18;
  const width = pageWidth - left * 2;
  let y = Math.max(startY, contentAreaTop);
  let context = '';

  function ensureSpace(height) {
    if (y + height > bottom) {
      doc.addPage();
      y = contentAreaTop;
    }
  }

  function text(value, { bold = false, color = [0, 0, 0], size = 9 } = {}) {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor(...color);
    for (const line of doc.splitTextToSize(display(value), width)) {
      ensureSpace(5);
      doc.text(line, left, y);
      y += 5;
    }
    doc.setTextColor(0, 0, 0);
    y += 2;
  }

  // Repeating the procedure/category and table title in the header keeps long
  // obstacle tables identifiable on subsequent pages. Margins reserve the same
  // header/footer space as the rest of the report.
  function table(title, columns, rows, { input = false, critical = [] } = {}) {
    if (!rows.length) return;
    ensureSpace(40);
    const totalWeight = columns.reduce((sum, column) => sum + (column.weight || 1), 0);
    const columnStyles = Object.fromEntries(columns.map((column, index) => [index, {
      cellWidth: width * (column.weight || 1) / totalWeight,
      halign: column.align || 'left',
      ...(column.bold ? { fontStyle: 'bold' } : {}),
    }]));
    doc.autoTable({
      startY: y,
      head: [[{
        content: context ? `${context}\n${title}` : title,
        colSpan: columns.length,
        styles: { halign: 'left', fontSize: 8 },
      }], columns.map(column => column.title)],
      body: rows,
      styles: { font: 'helvetica', fontSize: 7.5, cellPadding: 1.6, overflow: 'linebreak', minCellHeight: 6 },
      headStyles: {
        fillColor: input ? INPUT_BLUE : OUTPUT_BLUE,
        textColor: input ? [0, 0, 0] : [255, 255, 255],
        fontStyle: 'bold', fontSize: 7.5,
      },
      columnStyles,
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left, right: left, top: contentAreaTop, bottom: marginBottom + footerHeight },
      tableWidth: width,
      showHead: 'everyPage',
      rowPageBreak: 'avoid',
      pageBreak: rows.length < 4 ? 'avoid' : 'auto',
      theme: 'grid',
      didParseCell: data => {
        if (data.section === 'body' && critical[data.row.index]) {
          data.cell.styles.fillColor = [255, 235, 238];
          data.cell.styles.textColor = CRITICAL_RED;
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  function parameters(title, pairs, input = false) {
    const rows = [];
    for (let index = 0; index < pairs.length; index += 2) {
      rows.push([pairs[index][0], display(pairs[index][1]),
        pairs[index + 1]?.[0] || '', pairs[index + 1] ? display(pairs[index + 1][1]) : '']);
    }
    table(title, [
      { title: label('Parameter', 'Parameter'), weight: 35, bold: true },
      { title: label('Värde', 'Value'), weight: 52 },
      { title: label('Parameter', 'Parameter'), weight: 35, bold: true },
      { title: label('Värde', 'Value'), weight: 52 },
    ], rows, { input });
  }

  ensureSpace(62);
  text(label('GNSS Approach - indata och resultat', 'GNSS Approach - inputs and results'), { bold: true, size: 12 });
  const distanceFilter = analysis.summary?.distanceFilter;
  if (distanceFilter) {
    text(label(
      `Avståndsfilter: ${distanceFilter.radiusNm} NM från THR till något hinder (inklusive cirkelradie). `
        + `Valda procedurer: ${distanceFilter.proceduresSelected} av ${distanceFilter.proceduresAvailable}; `
        + `utanför avståndet: ${distanceFilter.proceduresExcluded}.`,
      `Distance filter: ${distanceFilter.radiusNm} NM from THR to any obstacle (including circle radius). `
        + `Procedures retained: ${distanceFilter.proceduresSelected} of ${distanceFilter.proceduresAvailable}; `
        + `outside range: ${distanceFilter.proceduresExcluded}.`,
    ));
    if (distanceFilter.proceduresWithoutReference > 0) {
      text(label(
        `${distanceFilter.proceduresWithoutReference} procedurer saknar användbara THR-koordinater; `
          + 'behållna för indatavalidering utan avståndsfiltrering.',
        `${distanceFilter.proceduresWithoutReference} procedures have no usable THR coordinates; `
          + 'retained for input validation without distance filtering.',
      ), { color: CRITICAL_RED });
    }
  }
  if (!results.length) {
    text(analysis.message || (allResults.length && allResults.every(result => !!result.input?.error)
      ? label('GNSS-beräkningarna kunde inte slutföras.',
        'GNSS calculations could not be completed.')
      : allResults.length
      ? label('Inga hinder hittades inom de beräknade GNSS-procedurernas skyddsytor.',
        'No obstacles found within the evaluated GNSS procedure protection areas.')
      : label('Inga GNSS-procedurer finns att redovisa.', 'No GNSS approach configurations found.')));
    return y;
  }
  text(label('Tabeller visas endast för procedurer och kategorier med hinder inom skyddsytorna.',
    'Tables show only procedures and categories with obstacles within the protection areas.'));

  const groups = new Map();
  for (const result of results) {
    const input = result.input || {};
    const key = JSON.stringify([input.id, input.airport_code, input.runway_designator, input.approach_id, input.approach_type]);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(result);
  }
  const hasConservativeDfReview = result => (result.manualReviewWarnings || [])
    .some(warning => warning.code === 'GNSS_CONSERVATIVE_DF');
  const status = result => result.input?.error
    ? label('Ej beräknad', 'Not evaluated')
    : result.manualReviewRequired ? hasConservativeDfReview(result)
      ? label('Indikerad avvikelse - manuell granskning', 'Indicated deviation - manual review')
      : label('Preliminär – manuell granskning', 'Preliminary – manual review')
    : obstacleCountsByResult.get(result).criticalObstacles > 0 ? label('Påverkan', 'Impact') : label('Ingen påverkan', 'No impact');

  for (const categories of groups.values()) {
    context = procedureLabel(categories[0].input || {}, false);
    if (categories.length > 1) {
      table(label('Kategoriöversikt', 'Category comparison'), [
        { title: 'Cat', weight: 10 },
        { title: label('Använd OCA (ft)', 'Used OCA (ft)'), weight: 25 },
        { title: label('Beräknad max OCA (ft)', 'Computed max OCA (ft)'), weight: 28 },
        { title: label('Beräknad max MOCA (ft)', 'Computed max MOCA (ft)'), weight: 28 },
        { title: label('Bedömda hinder', 'Obstacles assessed'), weight: 20 },
        { title: label('Kritiska', 'Critical'), weight: 15 },
        { title: 'Status', weight: 32 },
      ], categories.map(result => {
        const input = result.input || {};
        const counts = obstacleCountsByResult.get(result);
        return [display(input.aircraft_category), fixed(input.oca_unit === 'm' ? divide(input.oca, 0.3048) : input.oca, 1),
          fixed(result.summary?.maxOcaFt, 1), fixed(result.summary?.maxMocaFt, 1),
          input.error ? '-' : counts.obstaclesAssessed, input.error ? '-' : counts.criticalObstacles, status(result)];
      }), { critical: categories.map(result => !!result.input?.error || obstacleCountsByResult.get(result).criticalObstacles > 0) });
    }

    for (const result of categories) {
      const input = result.input || {};
      const segments = result.segments || [];
      const obstacleCounts = obstacleCountsByResult.get(result);
      const reviewColor = obstacleCounts.criticalObstacles > 0 ? CRITICAL_RED : [85, 85, 85];
      context = procedureLabel(input);
      ensureSpace(35);
      text(context, { bold: true, color: OUTPUT_BLUE, size: 10 });
      text(`${label('Flygplanskategori', 'Aircraft category')}: ${display(input.aircraft_category)}`
        + ` | ${label('Publicerade minima', 'Published minima')}: ${minimum({ value: input.oca, unit: input.oca_unit || 'ft' })}`,
      { bold: true });
      text(`${label('Totalt antal hinder bedömda', 'Total obstacles assessed')}: ${obstacleCounts.obstaclesAssessed}`
        + `\n${label('Kritiskt hinder', 'Critical obstacle')}: ${obstacleCounts.criticalObstacles}`);
      if (result.manualReviewRequired && hasConservativeDfReview(result)) {
        const paragraphs = [
          [label('Indikerad avvikelse från PANS-OPS - manuell granskning krävs',
            'Indicated deviation from PANS-OPS - manual review required'), { bold: true, color: reviewColor }],
          [label(
            'FlightSafe Analyzer har, utifrån angivna procedur- och flygplansdata, identifierat att den aktuella missed approach-svängen inte medger ett slutet skyddsområde med den ordinarie PANS-OPS-konstruktion som tillämpas i analysen.',
            'FlightSafe Analyzer has, based on the supplied procedure and aircraft data, identified that the protection area for the missed approach turn cannot be closed using the standard PANS-OPS construction applied in this analysis.'), { color: reviewColor }],
          [label(
            'FlightSafe Analyzer tillämpar därför en konservativ granskningsmetod där samtliga tre modellerade vindspiraler ingår. Full MOC och den lägsta tillämpliga referenshöjden används i hela granskningsytan. Ingen ytterligare höjdmarginal från stiggradienten tillgodoräknas.',
            'FlightSafe Analyzer therefore applies a conservative review method that includes all three modeled wind spirals. Full MOC and the lowest applicable reference altitude are used throughout the review area. No additional height margin from the climb gradient is taken into account.'), {}],
          [label(
            'Avvikelsen och resultatet ska verifieras manuellt mot PANS-OPS och procedurens ursprungliga konstruktionsunderlag.',
            "The indicated deviation and the result must be manually verified against PANS-OPS and the procedure's original design documentation."), { color: reviewColor }],
        ];
        const height = paragraphs.reduce((total, [value, options]) => {
          doc.setFont('helvetica', options.bold ? 'bold' : 'normal');
          doc.setFontSize(9);
          return total + doc.splitTextToSize(value, width).length * 5 + 2;
        }, 0);
        ensureSpace(height);
        for (const [value, options] of paragraphs) text(value, options);

        // Keep distinct construction details without repeating the old disclaimer
        // or printing the conservative-method explanation once for every leg.
        const notes = new Set();
        for (const warning of result.manualReviewWarnings || []) {
          if (warning.code === 'GNSS_CONSERVATIVE_DF') continue;
          const note = warning.code === 'GNSS_EXTENDED_SPIRAL'
            ? label('Konstruktionsuppgift: vindspiralen har utökats från 270 till 360 grader.',
              'Construction detail: the wind spiral has been extended from 270 to 360 degrees.')
            : warning.code === 'GNSS_DF_END_JOIN'
              ? label('En lokal tilläggsyta förenar DF-slutgränserna vid målfixen.',
                'A local additional area joins the DF end sections at the destination fix.')
              : String(warning.message || '')
                .replace(/Avvikelse från ordinarie PANS OPS-konstruktion; manuell kontroll mot PANS-OPS krävs\./g, '')
                .replace(/Departure from the standard PANS-OPS construction; manual review against PANS-OPS is required\./g, '')
                .trim();
          if (note) notes.add(note);
        }
        for (const note of notes) text(note, { color: reviewColor });
      } else if (result.manualReviewRequired) {
        text(label('Preliminärt resultat – manuell granskning krävs', 'Preliminary result – manual review required'), { bold: true, color: reviewColor });
        text(label('Avvikelse från ordinarie PANS OPS-konstruktion; manuell kontroll mot PANS-OPS krävs.',
          'Departure from the standard PANS-OPS construction; manual review against PANS-OPS is required.'), { color: reviewColor });
        for (const warning of result.manualReviewWarnings || []) {
          text(warning.message, { color: reviewColor });
          if (warning.code === 'GNSS_DF_END_JOIN') text(label(
            'En lokal tilläggsyta täcker urgröpningen mellan DF-slutgränserna. Full MOC. Ingen ytterligare höjdmarginal från stiggradienten tillgodoräknas inom tillägget.',
            'A local additional area covers the notch between the DF end sections. Full MOC and no additional climb credit within the addition.'));
        }
      }
      const source = input.oca_source?.table === 'oas_minima' ? ' (oas_minima)'
        : input.oca_source?.table === 'gnss_approach_inputs' ? label(' (procedurindata)', ' (procedure input)') : '';
      parameters(label('Indata', 'Input parameters'), [
        [label('Flygplats', 'Airport'), input.airport_code], ['RWY', input.runway_designator],
        ['Approach ID', input.approach_id], [label('Inflygningstyp', 'Approach type'), input.approach_type],
        ['RNAV specification', input.rnav_specification], ['Aircraft category', input.aircraft_category === 'DL' ? 'DL (D speeds)' : input.aircraft_category],
        ['OCA' + source, minimum({ value: input.oca, unit: input.oca_unit || 'ft' })],
        ['GPA/VPA (°)', display(input.gpa_vpa)],
        [label('MISAP svängtyp', 'MISAP turn type'), input.missed_straight_to_altitude
          ? label('Alt – rakt till höjd', 'Alt – straight to altitude') : input.missed_approach_turn_type],
        ['MISAP MACG (%)', input.missed_approach_climb_gradient],
        ['SOC altitude option', input.soc_altitude_option],
        [input.missed_straight_to_altitude ? label('Sluthöjd (ft AMSL)', 'End altitude (ft AMSL)')
          : label('Svänghöjd (ft AMSL)', 'Turn altitude (ft AMSL)'), input.missed_turn_altitude_ft],
      ], true);

      if (input.error) {
        table(label('Beräkningsfel', 'Evaluation error'), [{ title: 'Status' }, { title: label('Fel', 'Error'), weight: 4 }],
          [[status(result), display(input.error)]], { critical: [true] });
        continue;
      }

      const iasLabels = { iafwp1_ias_kt: 'IAF 1', iafwp2_ias_kt: 'IAF 2', iafwp3_ias_kt: 'IAF 3',
        iwp_ias_kt: 'IF', fawp_ias_kt: 'FAF / SOC', baro_ias_at_thr_kt: 'BARO THR', lpv_ias_at_thr_kt: 'LPV THR' };
      const iasRows = [
        ...Object.entries(input.ias_selection || {}).filter(([, selection]) => selection).map(([field, selection]) => ({
          name: iasLabels[field] || field, selection, plus10: false,
        })),
        ...(input.missed_legs || []).filter(leg => leg.ias_selection).map(leg => ({
          name: `MISAP leg ${leg.leg_no}`, selection: leg.ias_selection, plus10: leg.ias_plus10_percent === true,
        })),
      ];
      table(label('IAS-val (kt)', 'IAS selection (kt)'), [
        { title: 'Segment', weight: 25 }, { title: label('Indata', 'Input'), weight: 20 },
        { title: label('Kategoristandard', 'Category standard'), weight: 29 },
        { title: label('Vald bas-IAS', 'Selected base IAS'), weight: 25 }, { title: '+10%', weight: 15 },
        { title: label('Använd IAS', 'Used IAS'), weight: 23 },
      ], iasRows.map(({ name, selection, plus10 }) => [name, fixed(selection.inputKt, 1), fixed(selection.standardKt, 1),
        fixed(selection.iasKt, 1), yesNo(plus10), fixed(numeric(selection.iasKt) ? Number(selection.iasKt) * (plus10 ? 1.1 : 1) : null, 1)]));

      const legs = input.missed_legs || [];
      table(label('MISAP-ben - indata', 'MISAP legs - inputs'), [
        { title: label('Ben', 'Leg'), weight: 7 }, { title: label('Från / till', 'From / to'), weight: 25 },
        { title: label('Typ', 'Type'), weight: 15 },
        { title: label('Höjd (ft)', 'Altitude (ft)'), weight: 20 },
        { title: 'Bank nom / area (°)', weight: 24 }, { title: label('Vind', 'Wind'), weight: 22 },
        { title: label('Svängstart / riktning', 'Turn start / direction'), weight: 31 },
      ], legs.map((leg, index) => {
        const initial = leg.segment === 'initial_straight';
        // waypoint_type belongs to the previous leg's destination. It controls
        // the start of this leg, matching the backend/C# TypeTo convention.
        const previous = legs[index - 1];
        const startType = previous?.end_type || previous?.waypoint_type;
        return [display(leg.leg_no), `${display(leg.from)} / ${display(leg.to)}`,
          initial ? label('Rak', 'Straight') : display(leg.leg_type),
          initial ? '-' : leg.altitude_ft == null ? label('Automatisk', 'Automatic') : fixed(leg.altitude_ft, 1),
          initial ? '-' : `${fixed(leg.bank_nom_deg, 1)} / ${fixed(leg.bank_area_deg, 1)}`,
          leg.wind_type === 'Custom' ? `Custom ${fixed(leg.wind_custom_kt, 1)} kt` : display(leg.wind_type),
          initial ? '-' : `${display(startType)} / ${leg.turn_direction || (leg.leg_type === 'DF' ? 'Auto' : '-')}`];
      }), { input: true });

      const endpoint = result.missedEndpoint;
      if (endpoint) parameters(endpoint.pointRole === 'turn' ? label('MISAP-svängpunkt', 'Missed approach turn point')
        : label('MISAP-slutpunkt', 'Missed approach endpoint'), [
        [label('Punkt', 'Fix'), endpoint.source === 'altitude' ? endpoint.pointRole === 'turn'
          ? label('Beräknad höjdsvängpunkt', 'Calculated altitude turn point')
          : label('Beräknad höjdslutpunkt', 'Calculated altitude endpoint') : endpoint.ident || 'MAHWP/TP'],
        [label('Källa', 'Source'), endpoint.source === 'waypoint'
          ? label('Waypoint projicerad på finalaxeln', 'Waypoint projected onto final axis') : endpoint.source === 'altitude'
            ? label('SOC + stiggradient till angiven höjd', 'SOC + climb gradient to specified altitude') : label('Angivet avstånd', 'Entered distance')],
        [label('Avstånd från THR (NM)', 'Distance from THR (NM)'), `${fixed(endpoint.distanceNm, 4)} ${endpoint.beforeThr ? label('före', 'before') : label('efter', 'past')} THR`],
        [label('Sidoavvikelse (m)', 'Cross-track offset (m)'), endpoint.source === 'waypoint' ? fixed(endpoint.crossTrackM) : '-'],
      ]);

      const oas = result.lpv?.parameters || result.xls?.parameters;
      if (oas) {
        parameters(`${input.approach_type} - OAS`, [
          ['OAS category', oas.oasCategory], [label('Bedömningsmetod', 'Evaluation method'), oas.evaluationMethod],
          [label('FAP-höjd (ft)', 'FAP altitude (ft)'), fixed(divide(oas.altitudeAtFapM, 0.3048))], ['FAP distance (NM)', fixed(divide(oas.xfapM, 1852))],
          ['GARP -> LTP (m)', fixed(oas.garpM)], ['RDH (m)', fixed(oas.rdhM)],
          [label('Jordradie (m)', 'Earth radius (m)'), fixed(oas.earthRadiusM)],
          [oas.isXls ? label('Höjdförlustkälla', 'Height loss source') : 'IAS at THR (kt)',
            oas.isXls ? label('Kategori/manuellt värde', 'Category/manual input') : fixed(oas.iasAtThrKt)],
          [label('Höjdförlust (m)', 'Height loss (m)'), fixed(oas.heightLossM)], [label('Höjdförlustkorrektion (m)', 'Height loss correction (m)'), fixed(oas.heightLossCorrectionM)],
          ['Wing semi-span (m)', fixed(oas.k?.wssM)], ['Wheel height (m)', fixed(oas.k?.whM)],
          ['OAS MACG (%)', fixed(oas.oasMacgPct)], ['MISAP MACG (%)', fixed(oas.macgPct)],
          ['SOC altitude (ft)', fixed(divide(oas.socAltitudeM, 0.3048))],
          [label('MISAP-start från THR (NM)', 'Missed start from THR (NM)'), numeric(oas.missedStationM)
            ? `${fixed(Math.abs(oas.missedStationM) / 1852)} ${oas.missedStationM < 0 ? label('före', 'before') : label('efter', 'past')} THR` : '-'],
        ]);
        const constants = (title, k, includeWs) => table(title,
          [{ title: label('Yta', 'Surface') }, { title: 'A' }, { title: 'B' }, { title: 'C' }],
          [['W', 'WA', null, 'WC'], ...(includeWs ? [['WS', 'WSA', null, 'WSC']] : []),
            ['X', 'XA', 'XB', 'XC'], ['Y', 'YA', 'YB', 'YC'], ['Z', 'ZA', null, 'ZC']]
            .map(([name, a, b, c]) => [name, ...[a, b, c].map(key => key ? display(k?.[key]) : '-')]));
        constants(label('OAS-konstanter', 'OAS constants'), oas.k, true);
        if (oas.catIIConstants) constants(label('ILS CAT II-konstanter (inre OAS, 150 m)', 'ILS CAT II constants (inner OAS, 150 m)'), oas.catIIConstants, false);
      }

      const baro = result.baro?.parameters;
      if (baro) parameters('BARO/VNAV - OAS', [
        [label('Bedömningsmetod', 'Evaluation method'), baro.evaluationMethod], ['FAP altitude (ft)', fixed(divide(baro.altitudeAtFapM, 0.3048))],
        [label('Min temperatur (°C)', 'Minimum temperature (°C)'), fixed(baro.minTempC)], ['IAS at THR (kt)', fixed(baro.iasAtThrKt)],
        [label('Höjdförlust (m)', 'Height loss (m)'), fixed(baro.heightLossM)], [label('Höjdförlustkorrektion (m)', 'Height loss correction (m)'), fixed(baro.heightLossCorrectionM)],
        [label('Temperaturkorrektion (m)', 'Temperature correction (m)'), fixed(baro.temperatureCorrectionM)], ['Minimum VPA (°)', fixed(baro.minVpaDeg)],
        [label('Max temperatur (°C)', 'Maximum temperature (°C)'), fixed(baro.maxTempC)], ['XFAP (m)', fixed(baro.xfapM)],
        ['Xz (m)', fixed(baro.xzM)], ['SOC altitude (ft)', fixed(divide(baro.socAltitudeM, 0.3048))],
        ['MOC intermediate (m)', fixed(baro.mocIntermediateM)], ['MOC MISAP initial (m)', fixed(baro.mocMissedInitialM)],
        ['MOC MISAP final (m)', fixed(baro.mocMissedFinalM)],
      ]);

      const summary = result.summary || {};
      parameters(label('Resultatöversikt', 'Result summary'), [
        [label('Beräknad max MOCA', 'Computed max MOCA'), altitude(summary.maxMocaM, summary.maxMocaFt)],
        [label('Beräknad max OCA', 'Computed max OCA'), altitude(summary.maxOcaM, summary.maxOcaFt)],
        [label('Beräknade segment', 'Segments evaluated'), summary.segsEvaluated],
        [label('Totalt antal hinder bedömda', 'Total obstacles assessed'), obstacleCounts.obstaclesAssessed],
        [label('Kritiskt hinder', 'Critical obstacle'), obstacleCounts.criticalObstacles], ['Status', status(result)],
      ]);

      if (segments.some(segment => segment.ifTurn)) text(label(
        'Intermediate – konstruktionsdata för IF-svängar', 'Intermediate – IF turn construction data'), { bold: true });
      for (const segment of segments.filter(segment => segment.ifTurn)) {
        const p = segment.ifTurn;
        parameters(`${display(segment.segLabel)} - IF turn`, [
          ['IAS (kt)', fixed(p.iasKt, 1)], ['TAS (kt)', fixed(p.tasKt, 1)],
          [label('Vind (kt)', 'Wind (kt)'), fixed(p.windKt, 1)], ['TAS altitude (ft)', fixed(p.altitudeFt)],
          [label('Referens', 'Reference'), `${display(p.referenceType)} (${display(p.referenceIdent)})`],
          [label('Referenshöjd (ft)', 'Reference altitude (ft)'), fixed(p.referenceAltitudeFt)],
          [label('Avstånd till IF (NM)', 'Distance to IF (NM)'), fixed(p.distanceNm, 3)], [label('Gradient (%)', 'Gradient (%)'), fixed(p.gradientPercent, 1)],
          [label('Höjdtillägg (ft)', 'Height gain (ft)'), fixed(p.heightGainFt)],
        ]);
      }

      for (const segment of gnssResultSegments(segments, sv)) {
        const rows = segment.obsRows || [];
        if (!rows.length) continue;
        const family = String(segment.segFamily || '').toLowerCase();
        const isOas = !!(segment.baroSurface || segment.lpvSurface || segment.xlsSurface);
        const isOasMissed = family === 'missed' && !!segment.missedApproach && segment.missedResultPhase !== 'initial';
        const altitudeAssessment = isOasMissed && (segment.missedResultPhase === 'turn_initial'
          || rows.every(row => row.assessmentMethod === 'full_moc_no_climb_credit'));
        const isFinal = family === 'final' && !isOas;
        const usesMoca = family === 'initial' || family === 'intermediate';
        const calculated = usesMoca ? label('Beräknad MOCA', 'Calculated MOCA')
          : isOasMissed ? label('Erforderlig höjd', 'Required altitude') : label('Beräknad OCA', 'Calculated OCA');
        const clearance = isOas ? label('Marginal till minima', 'Minima clearance')
          : altitudeAssessment ? label('Höjdmarginal', 'Altitude clearance')
          : isOasMissed ? label('Stigmarginal', 'Climb clearance') : label('Marginal', 'Clearance');
        const title = `${display(segment.segLabel || segment.segType)} (${segmentRoute(segment, input)})`
          + (segment.missedResultPhase ? ` – ${gnssMocLabel(segment, sv)}` : '');
        const missedParameters = gnssMissedParameters(segment, sv);
        if (missedParameters.length) parameters(`${title} – ${label('Beräkningsdata', 'Calculation data')}`, missedParameters);
        const critical = rows.map(row => !!row.isCritical);
        // Split wide HTML tables into aligned result/detail tables. Row numbers
        // identify repeated obstacle names/areas without shrinking the PDF text.
        const identityColumns = [{ title: '#', weight: 7 }, { title: label('Hinder', 'Obstacle'), weight: 28 }];
        table(title, [
          ...identityColumns, { title: label('Område', 'Area'), weight: 17 },
          { title: label('Höjd (m / ft)', 'Elevation (m / ft)'), weight: 20 }, { title: 'MOC (m)', weight: 13 },
          { title: `${calculated} (m / ft)`, weight: 23 },
          { title: usesMoca ? label('Publicerad MOCA', 'Published MOCA') : label('Publicerad OCA', 'Published OCA'), weight: 22 },
          ...(isFinal ? [{ title: 'Step-down', weight: 18 }] : []),
          { title: `${clearance} (m / ft)`, weight: 22 }, { title: label('Kritisk', 'Critical'), weight: 13 },
        ], rows.map((row, index) => {
          const published = row.publishedMinimum || segment.publishedMinimum;
          const stepdown = published?.stepdownApplied === true || published?.stepdownDistanceNm != null;
          return [String(index + 1), display(row.obstacleName), display(row.areaType), altitude(row.elevM, row.elevFt), fixed(row.mocM),
            altitude(row.ocaM, row.ocaFt), minimum(published),
            ...(isFinal ? [stepdown ? `${yesNo(true)}${published?.stepdownDistanceNm != null ? ` (${published.stepdownDistanceNm} NM)` : ''}` : yesNo(false)] : []),
            altitude(row.clearanceM, row.clearanceFt), yesNo(row.isCritical === true)];
        }), { critical });

        if (isOas) table(`${title} - ${label('OAS-resultat', 'OAS details')}`, [
          ...identityColumns,
          ...(segment.xlsSurface ? [{ title: 'OAS category', weight: 20 }, { title: label('Sekundärkorrektion (m)', 'Secondary correction (m)'), weight: 23 }] : []),
          { title: label('Ythöjd (m)', 'Surface altitude (m)'), weight: 24 },
          { title: label('Differens (m)', 'Difference (m)'), weight: 22 },
          { title: label('Ekvivalent höjd (m)', 'Equivalent altitude (m)'), weight: 25 },
          { title: label('Höjdförlust (m)', 'Height loss (m)'), weight: 23 },
        ], rows.map((row, index) => [String(index + 1), display(row.obstacleName),
          ...(segment.xlsSurface ? [display(row.oasCategory), fixed(row.secondaryCorrectionM)] : []),
          fixed(row.surfaceAltitudeM), fixed(row.differenceM), fixed(row.equivalentAltitudeM), fixed(row.heightLossM)]), { critical });

        if (isOasMissed) table(`${title} - ${label('Stigbedömning', 'Climb assessment')}`, [
          ...identityColumns, { title: label('Tillgänglig höjd (m)', 'Available altitude (m)'), weight: 28 },
          { title: label('Erforderlig MACG (%)', 'Required MACG (%)'), weight: 28 },
          { title: label('Tillgänglig MACG (%)', 'Available MACG (%)'), weight: 28 },
        ], rows.map((row, index) => [String(index + 1), display(row.obstacleName),
          fixed(row.availableAltitudeM), fixed(row.requiredMacgPercent), fixed(row.availableMacgPercent)]), { critical });
      }

      const warnings = [...new Set([
        ...(result.baro?.validationWarnings || []), ...(result.lpv?.validationWarnings || []), ...(result.xls?.validationWarnings || []),
        ...segments.flatMap(segment => segment.missedApproach?.validationWarnings || []),
      ])];
      for (const warning of warnings) text(`${context}\n${label('Beräkningsanmärkning', 'Calculation note')}: ${warning}`);
      y += 4;
    }
  }
  return y;
}
